import React from "react";
import styled from "styled-components";
import { Button, Grid, Tooltip } from "@mui/material";
import { AddLink, LinkOff } from "@mui/icons-material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import Icon from "../../components/icon";
import Flex from "../../components/flex";

const ButtonWithBorder = styled(Button)`
    ${({ theme, checked }) =>
        checked
            ? `border: 4px solid ${theme.palette.primary.main}`
            : `border: 4px solid transparent`};
`;

const Integrated = styled(Flex).attrs(() => ({
    justifyContent: "center",
}))`
    position: absolute;
    top: -4px;
    right: -4px;
    height: 40px;
    width: 40px;
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom-left-radius: 8px;
`;

const CentrallyManaged = styled(Flex).attrs(() => ({
    justifyContent: "center",
}))`
    position: absolute;
    top: -4px;
    left: 126px;
    height: 40px;
    width: 40px;
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom-left-radius: 8px;
`;

const PendingInstallations = styled(Flex).attrs(() => ({
    justifyContent: "center",
}))`
    position: absolute;
    top: -4px;
    right: 126px;
    height: 40px;
    width: 40px;
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom-right-radius: 8px;
`;

const FutureIntegration = styled(Flex).attrs(() => ({
    justifyContent: "center",
}))`
    position: absolute;
    top: -4px;
    height: 40px;
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    text-transform: capitalize;
    padding-left: ${({ theme }) => theme.spacing(3)};
    padding-right: ${({ theme }) => theme.spacing(3)};
    color: ${({ theme }) => theme.palette.primary.main};
`;

const IntegrationButton = ({
    svg,
    action,
    checked,
    connected,
    centrallyManaged,
    invalid,
    invalidationReason,
    pendingInstallations,
    integrationType,
    future,
    maintenance,
    disabled,
    name,
}) => {
    const getAdditionalExplainerText = () => {
        if (!disabled && (centrallyManaged || integrationType === "app")) {
            if (!connected && (centrallyManaged || integrationType === "app")) {
                return "Your emails could not be matched.";
            }
            return "Connected";
        }
        if (!disabled && connected && !centrallyManaged && integrationType !== "app") {
            if (invalid) {
                return invalidationReason;
            }
            return "Integrated";
        }
        if (connected && pendingInstallations > 0) {
            return `You have ${pendingInstallations} pending installations`;
        }
        if (future) return "coming soon";
        if (maintenance) return "under maintenance";
        return "";
    };

    const additionalExplainerText = getAdditionalExplainerText();

    return (
        <Grid container item spacing={2}>
            <Grid item>
                <ButtonWithBorder
                    aria-label={
                        additionalExplainerText ? `${name}: ${additionalExplainerText}` : name
                    }
                    checked={checked}
                    variant="contained"
                    onClick={action}
                    style={{
                        width: "170px",
                        height: "93px",
                    }}
                    color="secondary"
                    disabled={future || maintenance || disabled}
                >
                    {!disabled && (centrallyManaged || integrationType === "app") && (
                        <CentrallyManaged>
                            {!connected && (centrallyManaged || integrationType === "app") ? (
                                <Tooltip title="Your emails could not be matched.">
                                    <ManageAccountsIcon color="warning" />
                                </Tooltip>
                            ) : (
                                <Tooltip describeChild title="Connected">
                                    <ManageAccountsIcon color="primary" />
                                </Tooltip>
                            )}
                        </CentrallyManaged>
                    )}
                    {!disabled && connected && !centrallyManaged && integrationType !== "app" && (
                        <Integrated>
                            {invalid ? (
                                <Tooltip title={invalidationReason}>
                                    <LinkOff color="warning" />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Integrated">
                                    <AddLink color="primary" />
                                </Tooltip>
                            )}
                        </Integrated>
                    )}
                    {connected && pendingInstallations > 0 && (
                        <PendingInstallations>
                            <Tooltip
                                title={`You have ${pendingInstallations} pending installations`}
                            >
                                <HourglassTopIcon color="info" />
                            </Tooltip>
                        </PendingInstallations>
                    )}
                    {future && <FutureIntegration>coming soon</FutureIntegration>}
                    {maintenance && <FutureIntegration>under maintenance</FutureIntegration>}
                    <Icon svg={svg} width="100px" height="127" />
                </ButtonWithBorder>
            </Grid>
        </Grid>
    );
};

export default IntegrationButton;
